import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "./UserContext";
import {
  createBlog as createBlogRequest,
  deleteBlog as deleteBlogRequest,
  fetchBlogsByUser,
} from "./services/blogService";
import { buildBlogPayload, getBlogId, isBlogAdmin } from "./utils/blogUtils";

const BlogContext = createContext();

export const BlogProvider = ({ children }) => {
  const [blogs, setBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const refreshBlogs = useCallback(async () => {
    if (!user?.user_id || !isBlogAdmin(user)) {
      setBlogs([]);
      return [];
    }

    setIsLoading(true);
    try {
      const data = await fetchBlogsByUser(user.user_id);
      const sortedBlogs = (data ?? []).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setBlogs(sortedBlogs);
      return sortedBlogs;
    } catch (error) {
      console.error("Error fetching blogs:", error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refreshBlogs();
  }, [refreshBlogs]);

  const addBlog = useCallback(
    async (blogData, selectedTags) => {
      const createdBlog = await createBlogRequest(
        buildBlogPayload({ blog: blogData, user, tags: selectedTags }),
        user?.access_token
      );

      setBlogs((prev) => [createdBlog, ...prev]);
      return createdBlog;
    },
    [user]
  );

  const deleteBlog = useCallback(
    async (blogId) => {
      await deleteBlogRequest(blogId, user?.access_token);
      setBlogs((prev) => prev.filter((blog) => getBlogId(blog) !== blogId));
      navigate("/blogPage");
    },
    [navigate, user?.access_token]
  );

  const value = useMemo(
    () => ({
      blogs,
      isLoading,
      refreshBlogs,
      addBlog,
      deleteBlog,
    }),
    [blogs, isLoading, refreshBlogs, addBlog, deleteBlog]
  );

  return <BlogContext.Provider value={value}>{children}</BlogContext.Provider>;
};

export const useBlogContext = () => useContext(BlogContext);
